import PlanoAcaoResultadoConsulta from '../types/negocio/PlanoAcaoResultadoConsulta';

/**
 * Calcula o percentual de conclusão de um Plano de Ação conforme as regras de negócio:
 * 
 * - Pendente, Planejada, Cancelada → 0%
 * - Finalizada → 100%
 * - Em Andamento → (tarefasFinalizadas / totalTarefas) * 100
 *   - Se não houver tarefas, retorna 0%
 * 
 * @param plano - O plano de ação para calcular o percentual
 * @returns Percentual de conclusão (0-100)
 */
export function calcularPercentualConclusao(plano: PlanoAcaoResultadoConsulta): number {
  const statusLower = plano.status?.toLowerCase() || '';

  // Status com conclusão fixa em 0%
  if (
    statusLower.includes('pendente') ||
    statusLower.includes('planejada') ||
    statusLower.includes('cancelada')
  ) {
    return 0;
  }

  // Status com conclusão fixa em 100%
  if (statusLower.includes('finalizada')) {
    return 100;
  }

  // Status "Em Andamento" - cálculo proporcional baseado nas tarefas
  if (statusLower.includes('em andamento')) {
    const totalTarefas = plano.totalTarefas ?? 0;
    const tarefasFinalizadas = plano.tarefasFinalizadas ?? 0;

    // Se não houver tarefas, considera 0%
    if (totalTarefas === 0) {
      return 0;
    }

    // Cálculo: (TarefasFinalizadas / TotalTarefas) * 100
    const percentual = (tarefasFinalizadas / totalTarefas) * 100;
    
    // Retorna valor arredondado para inteiro
    return Math.round(percentual);
  }

  // Status desconhecido - retorna 0% por segurança
  return 0;
}

/**
 * Formata o percentual para exibição (ex: "67%")
 * @param percentual - Valor numérico do percentual (0-100)
 * @returns String formatada com o símbolo de percentual
 */
export function formatarPercentual(percentual: number): string {
  return `${percentual}%`;
}

/**
 * Retorna a cor apropriada para a progress bar baseada no percentual
 * @param percentual - Valor numérico do percentual (0-100)
 * @returns Cor para usar na progress bar
 */
export function getCorProgressBar(percentual: number): 'error' | 'warning' | 'info' | 'success' {
  if (percentual === 0) return 'error';
  if (percentual < 30) return 'warning';
  if (percentual < 70) return 'info';
  return 'success';
}
